import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import axios, { AxiosError } from "axios";

import { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import CreateAccountVisiteur from "../../Views/Utilisateure/visiteur/CreateAccountVisiteur";

/* ─────────────────────────────────────────────────────────
   OTP Step overlay — reusable inline component
───────────────────────────────────────────────────────── */
function OtpStep({
  email,
  onVerified,
  onBack,
  type,
}: {
  email: string;
  onVerified: () => void;
  onBack: () => void;
  type: "compte" | "magasin";
}) {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleChange = (val: string, idx: number) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...code];
    next[idx] = val.slice(-1);
    setCode(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const verify = async () => {
    const fullCode = code.join("");
    if (fullCode.length < 6) {
      setError("Veuillez entrer les 6 chiffres.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("code", fullCode);
      const { data } = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/verify-email-code`,
        fd
      );
      if (data.valid) {
        onVerified();
      } else {
        setError("Code incorrect ou expiré. Vérifiez et réessayez.");
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch {
      setError("Erreur de connexion. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (countdown > 0) return;
    setResending(true);
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("name", "Utilisateur");
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/send-verification-code`,
        fd
      );
      setCountdown(60);
      setCode(["", "", "", "", "", ""]);
      setError("");
      inputRefs.current[0]?.focus();
    } catch {
      setError("Impossible de renvoyer le code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Back */}
        <button style={styles.backBtn} onClick={onBack}>
          ← Retour
        </button>

        {/* Icon */}
        <div style={styles.iconWrap}>📧</div>

        <h2 style={styles.title}>Vérification de l'email</h2>
        <p style={styles.sub}>
          Nous avons envoyé un code à 6 chiffres à<br />
          <strong style={{ color: "#4f46e5" }}>{email}</strong>
        </p>

        {/* 6-box OTP input */}
        <div style={styles.otpRow} onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              style={{
                ...styles.otpBox,
                borderColor: digit ? "#4f46e5" : "#d1d5db",
                boxShadow: digit ? "0 0 0 3px rgba(79,70,229,.15)" : "none",
              }}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {error && <p style={styles.errTxt}>⚠️ {error}</p>}

        <button
          style={{
            ...styles.verifyBtn,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={verify}
          disabled={loading}
        >
          {loading ? "Vérification…" : "Confirmer le code"}
        </button>

        <p style={styles.resendRow}>
          Vous n'avez pas reçu le code ?{" "}
          <button
            style={{
              ...styles.resendBtn,
              opacity: countdown > 0 ? 0.5 : 1,
              cursor: countdown > 0 ? "not-allowed" : "pointer",
            }}
            onClick={resend}
            disabled={countdown > 0 || resending}
          >
            {resending
              ? "Envoi…"
              : countdown > 0
              ? `Renvoyer (${countdown}s)`
              : "Renvoyer le code"}
          </button>
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Success screen
───────────────────────────────────────────────────────── */
function SuccessScreen({ type }: { type: "compte" | "magasin" }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = React.useState(3);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          navigate("/connecter_compte");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.card, textAlign: "center" }}>
        <div style={styles.successIcon}>✅</div>
        <h2 style={styles.title}>Compte créé avec succès !</h2>
        <p style={styles.sub}>
          Votre compte est maintenant <strong style={{ color: "#10b981" }}>actif</strong>.
          <br />
          Un email de confirmation a été envoyé à votre adresse.
        </p>
        <p style={{ ...styles.sub, color: "#6366f1", fontWeight: 600 }}>
          Redirection vers la connexion dans {countdown}s…
        </p>
        <button style={styles.verifyBtn} onClick={() => navigate("/connecter_compte")}>
          Se connecter maintenant
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Main Service Component
───────────────────────────────────────────────────────── */
export default function CreateAcoountVisiteurService() {
  const navigate = useNavigate();

  // Form state
  const [Nom_Complet, setNom_Complet] = useState<string>("");
  const [Adresse, setAdresse] = useState<string>("");
  const [Password, setPassword] = useState<string>("");
  const [EmailAccount, setEmailAccount] = useState<string>("");
  const [Tele, setTele] = useState<string>("");

  // UI feedback
  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [ShowEreurPopup, setShowEreurPopup] = useState<boolean>(false);
  const [ContentMessage, setContentMessage] = useState("Oops !");
  const [ParagrapheMessage, setParagrapheMessage] = useState("");

  // OTP step: "form" | "otp" | "creating" | "done"
  const [step, setStep] = useState<"form" | "otp" | "creating" | "done">("form");
  const [sendingOtp, setSendingOtp] = useState(false);

  /* ── Normalize phone ── */
  const normalizeMoroccanPhone = (phone: string): string | null => {
    let cleaned = phone.replace(/[\s+-]/g, "");
    if (/^0\d{9}$/.test(cleaned)) return "212" + cleaned.substring(1);
    if (/^212\d{9}$/.test(cleaned)) return cleaned;
    return null;
  };

  /* ── Step 1: Validate form & send OTP ── */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const normalizedTele = normalizeMoroccanPhone(Tele);

    if (!normalizedTele) {
      setShowEreurPopup(true);
      setContentMessage("Numéro de téléphone invalide");
      setParagrapheMessage("Veuillez entrer un numéro marocain valide.");
      setTimeout(() => setShowEreurPopup(false), 3000);
      return;
    }
    if (Adresse.trim() === "") {
      setShowEreurPopup(true);
      setContentMessage("Adresse obligatoire");
      setParagrapheMessage("Veuillez entrer votre adresse.");
      setTimeout(() => setShowEreurPopup(false), 3000);
      return;
    }
    if (!EmailAccount.trim() || !/\S+@\S+\.\S+/.test(EmailAccount)) {
      setShowEreurPopup(true);
      setContentMessage("Email invalide");
      setParagrapheMessage("Veuillez entrer une adresse email valide.");
      setTimeout(() => setShowEreurPopup(false), 3000);
      return;
    }

    // Send OTP
    setSendingOtp(true);
    try {
      const fd = new FormData();
      fd.append("email", EmailAccount);
      fd.append("name", Nom_Complet || "Utilisateur");
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/send-verification-code`,
        fd
      );
      setStep("otp");
    } catch {
      setShowEreurPopup(true);
      setContentMessage("Erreur d'envoi");
      setParagrapheMessage("Impossible d'envoyer le code de vérification.");
      setTimeout(() => setShowEreurPopup(false), 3000);
    } finally {
      setSendingOtp(false);
    }
  };

  /* ── Step 2 → 3: OTP verified → create account ── */
  const onOtpVerified = async () => {
    const normalizedTele = normalizeMoroccanPhone(Tele)!;
    const formData = new FormData();
    formData.append("Tele", normalizedTele);
    formData.append("Nom_Complet", Nom_Complet);
    formData.append("Adresse", Adresse);
    formData.append("Password", Password);
    formData.append("EmailAccount", EmailAccount);

    setStep("creating"); // show loader
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/créer_compte`,
        formData
      );
      if (data.message === "false") {
        setStep("form");
        setShowEreurPopup(true);
        setContentMessage("Compte déjà existant");
        setParagrapheMessage("Ce téléphone ou email est déjà utilisé.");
        setTimeout(() => setShowEreurPopup(false), 3000);
      } else {
        setStep("done");
      }
    } catch {
      setStep("form");
      setShowEreurPopup(true);
      setContentMessage("Erreur lors de la création du compte.");
      setParagrapheMessage("Veuillez réessayer.");
      setTimeout(() => setShowEreurPopup(false), 3000);
    }
  };

  /* ── Render ── */
  if (step === "creating") {
    return (
      <div style={styles.wrapper}>
        <div style={{ ...styles.card, textAlign: "center" as const }}>
          <div style={creatingLoaderStyle.spinner} />
          <h2 style={{ ...styles.title, marginTop: "24px" }}>Création du compte…</h2>
          <p style={styles.sub}>Veuillez patienter, votre compte est en cours de création.</p>
        </div>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <OtpStep
        email={EmailAccount}
        onVerified={onOtpVerified}
        onBack={() => setStep("form")}
        type="compte"
      />
    );
  }

  if (step === "done") {
    return <SuccessScreen type="compte" />;
  }

  return (
    <CreateAccountVisiteur
      ParagrapheMessage={ParagrapheMessage}
      setParagrapheMessage={setParagrapheMessage}
      ContentMessage={ContentMessage}
      setContentMessage={setContentMessage}
      Nom_Complet={Nom_Complet}
      setAdresse={setAdresse}
      setNom_Complet={setNom_Complet}
      setPassword={setPassword}
      setEmailAccount={setEmailAccount}
      EmailAccount={EmailAccount}
      setTele={setTele}
      Tele={Tele}
      Password={Password}
      showSuccessPopup={showSuccessPopup}
      setShowSuccessPopup={setShowSuccessPopup}
      ShowEreurPopup={ShowEreurPopup}
      setShowEreurPopup={setShowEreurPopup}
      Adresse={Adresse}
      handleSubmit={handleSubmit}
      sendingOtp={sendingOtp}
    />
  );
}

/* ─────────────────────────────────────────────────────────
   Shared inline styles
───────────────────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)",
    padding: "24px 16px",
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "48px 44px",
    width: "100%",
    maxWidth: "480px",
    boxShadow: "0 20px 60px rgba(79,70,229,.15)",
    border: "1px solid rgba(79,70,229,.1)",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#6366f1",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 500,
    padding: 0,
    marginBottom: "24px",
    display: "block",
  },
  iconWrap: {
    fontSize: "48px",
    textAlign: "center",
    display: "block",
    marginBottom: "12px",
  },
  successIcon: {
    fontSize: "56px",
    display: "block",
    marginBottom: "16px",
  },
  title: {
    textAlign: "center",
    fontSize: "1.6rem",
    fontWeight: 700,
    color: "#1e1b4b",
    margin: "0 0 10px",
  },
  sub: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: "0.92rem",
    lineHeight: 1.6,
    margin: "0 0 28px",
  },
  otpRow: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    marginBottom: "20px",
  },
  otpBox: {
    width: "52px",
    height: "58px",
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 700,
    border: "2px solid #d1d5db",
    borderRadius: "12px",
    color: "#1e1b4b",
    outline: "none",
    transition: "border-color .2s, box-shadow .2s",
  },
  errTxt: {
    textAlign: "center",
    color: "#ef4444",
    fontSize: "0.85rem",
    margin: "0 0 16px",
  },
  verifyBtn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    border: "none",
    borderRadius: "12px",
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "opacity .2s",
    marginBottom: "16px",
  },
  resendRow: {
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#6b7280",
    margin: 0,
  },
  resendBtn: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.85rem",
    padding: 0,
  },
};

/* Spinner style for the "creating" loader step */
const spinnerKeyframes = `
@keyframes wyn-spin { to { transform: rotate(360deg); } }
`;
if (typeof document !== "undefined" && !document.getElementById("wyn-spin-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "wyn-spin-style";
  styleEl.textContent = spinnerKeyframes;
  document.head.appendChild(styleEl);
}

const creatingLoaderStyle: Record<string, React.CSSProperties> = {
  spinner: {
    width: "64px",
    height: "64px",
    border: "6px solid #e0e7ff",
    borderTop: "6px solid #4f46e5",
    borderRadius: "50%",
    animation: "wyn-spin 0.8s linear infinite",
    margin: "0 auto",
  },
};